/**
 * Cloud Provider Hook Wrapper
 * Phase 3: State Management Migration - Backward Compatibility Layer
 * 
 * Provides backward compatibility wrapper that maintains existing useState interface
 * for seamless migration of components from useState to Zustand store
 */

import { useCloudProviderStore } from '@/stores/cloudProviderStore'
import type { CloudProvider } from '@/types/cloudProvider'

/**
 * Backward compatibility hook that mimics useState interface
 * 
 * This hook provides the same interface as useState for cloud provider selection,
 * allowing existing components to migrate with minimal code changes.
 * 
 * @returns [provider, setProvider] tuple identical to useState
 * 
 * @example
 * // Before migration (useState)
 * const [activeProvider, setActiveProvider] = useState<'aws' | 'gcp' | 'azure'>('aws')
 * 
 * // After migration (useCloudProvider)
 * const [activeProvider, setActiveProvider] = useCloudProvider()
 */
export const useCloudProvider = (): [CloudProvider, (provider: CloudProvider) => void] => {
  const provider = useCloudProviderStore((state) => state.provider)
  const setProvider = useCloudProviderStore((state) => state.setProvider)
  
  return [provider, setProvider] as const
}

/**
 * Enhanced hook for components that need additional provider information
 * 
 * Provides direct access to provider configuration and display utilities
 * for components that need more than just the provider ID.
 * 
 * @returns Object with provider state and configuration utilities
 * 
 * @example
 * const { provider, setProvider, config, displayName } = useCloudProviderEnhanced()
 */
export const useCloudProviderEnhanced = () => {
  const provider = useCloudProviderStore((state) => state.provider)
  const setProvider = useCloudProviderStore((state) => state.setProvider)
  const getProviderConfig = useCloudProviderStore((state) => state.getProviderConfig)
  const getProviderDisplayName = useCloudProviderStore((state) => state.getProviderDisplayName)
  const getProviderColor = useCloudProviderStore((state) => state.getProviderColor)
  
  return {
    provider,
    setProvider,
    config: getProviderConfig(),
    displayName: getProviderDisplayName(),
    color: getProviderColor()
  }
}

/**
 * Hook for components that only need to read provider state (no updates)
 * 
 * Optimized for read-only components that display provider information
 * but don't need to change the provider selection.
 * 
 * @returns Provider state and configuration in read-only format
 */
export const useCloudProviderReadonly = () => {
  const provider = useCloudProviderStore((state) => state.provider)
  const getProviderConfig = useCloudProviderStore((state) => state.getProviderConfig)
  const getProviderDisplayName = useCloudProviderStore((state) => state.getProviderDisplayName)
  
  return {
    provider,
    config: getProviderConfig(),
    displayName: getProviderDisplayName()
  }
}

/**
 * Hook for provider actions only (useful for components that only trigger changes)
 * 
 * Provides only the action functions without subscribing to state changes,
 * preventing unnecessary re-renders in components that only trigger updates.
 * 
 * @returns Object with provider action functions
 */
export const useCloudProviderActions = () => {
  const setProvider = useCloudProviderStore((state) => state.setProvider)
  const resetProvider = useCloudProviderStore((state) => state.resetProvider)
  
  return {
    setProvider,
    resetProvider
  }
}